/**
 * Adaptador de exportação da Análise de Atrasos: payload de
 * GET /api/producao/atrasos/analise → shape `{titulo, resumo, secoes}` de
 * utils/exportacao (mesmo padrão do Financeiro). Exporta o RECORTE atual
 * (lente + filtros ecoados pelo backend), incluindo a página corrente da
 * tabela-fonte.
 */
import { salvarCSV, salvarExcel, salvarPDF } from "../../../utils/exportacao";
import { LABEL_CATEGORIA } from "../categorias6m";

const SAVERS = { pdf: salvarPDF, excel: salvarExcel, csv: salvarCSV };

const COLUNAS_RANKING = [
  { key: "nome", label: "Nome", type: "text" },
  { key: "peso", label: "Peso", type: "number" },
  { key: "pct", label: "%", type: "percentage" },
  { key: "ocorrencias", label: "Ocorrências", type: "number" },
];

function linhasRanking(linhas, ehTempo) {
  return (linhas || []).map((r) => ({
    nome: r.nome || "",
    peso: ehTempo ? r.total_min ?? 0 : r.ocorrencias ?? 0,
    pct: r.pct ?? 0,
    ocorrencias: r.ocorrencias ?? 0,
  }));
}

export function exportarAnalise(formato, payload) {
  const saver = SAVERS[formato];
  if (!saver) throw new Error(`Formato desconhecido: ${formato}`);
  if (!payload) return null;

  const ehTempo = payload.lente === "tempo";
  const kpis = payload.kpis || {};
  const filtros = payload.filtros || {};

  const resumo = [
    { label: "Lente", value: ehTempo ? "Atraso (min)" : "Qualidade (FPY/defeitos)", type: "text" },
    { label: "Período", value: `${payload.periodo?.inicio || ""} a ${payload.periodo?.fim || ""}`, type: "text" },
    { label: "Fase", value: filtros.fase || "todas", type: "text" },
    { label: "Etapa", value: filtros.etapa || "todas", type: "text" },
    { label: "Categoria (6M)", value: LABEL_CATEGORIA[filtros.categoria] || "todas", type: "text" },
    { label: "Pedido", value: filtros.pedido || "todos", type: "text" },
  ];
  if (ehTempo) {
    resumo.push(
      { label: "Atraso total (min)", value: kpis.total_min ?? 0, type: "number" },
      { label: "Ocorrências", value: kpis.ocorrencias ?? 0, type: "number" },
      { label: "Atraso médio (min)", value: kpis.medio_min ?? "", type: "number" },
      { label: "Custo estimado", value: Number(kpis.custo_rs || 0), type: "currency" },
      { label: "Custo-hora (parâmetro)", value: Number(payload.parametros?.custo_hora || 0), type: "currency" },
    );
  } else {
    resumo.push(
      { label: "FPY %", value: kpis.fpy_pct ?? "", type: "percentage" },
      { label: "Taxa de retrabalho %", value: kpis.taxa_retrabalho_pct ?? "", type: "percentage" },
      { label: "Peças apresentadas", value: kpis.pecas_apresentadas ?? 0, type: "number" },
      { label: "Não-conformidades", value: kpis.nao_conformidades ?? 0, type: "number" },
      { label: "Custo de retrabalho", value: Number(kpis.custo_retrabalho_rs || 0), type: "currency" },
    );
  }

  const chavePeso = ehTempo ? "total_min" : "ocorrencias";
  const rotuloPeso = ehTempo ? "Atraso (min)" : "Ocorrências";

  const secoes = [
    {
      id: "fishbone",
      label: "Ishikawa — peso por categoria (6M)",
      columns: [
        { key: "categoria", label: "Categoria", type: "text" },
        { key: "peso", label: rotuloPeso, type: "number" },
        { key: "pct", label: "%", type: "percentage" },
        { key: "ocorrencias", label: "Ocorrências", type: "number" },
      ],
      rows: (payload.fishbone?.categorias || []).map((c) => ({
        categoria: c.label,
        peso: c[chavePeso] ?? 0,
        pct: c.pct ?? 0,
        ocorrencias: c.ocorrencias ?? 0,
      })),
    },
    {
      id: "pareto",
      label: ehTempo ? "Pareto por causa específica" : "Pareto por tipo de defeito",
      columns: [
        { key: "causa", label: ehTempo ? "Causa" : "Tipo de defeito", type: "text" },
        { key: "peso", label: rotuloPeso, type: "number" },
        { key: "pct", label: "%", type: "percentage" },
        { key: "pct_acumulado", label: "% acumulado", type: "percentage" },
      ],
      rows: (
        (ehTempo ? payload.pareto?.por_causa : payload.pareto?.por_tipo) || []
      ).map((linha) => ({
        causa: ehTempo
          ? `${linha.etapa_label ? `${linha.etapa_label}: ` : ""}${linha.descricao || "(sem descrição)"}`
          : linha.tipo || "(sem tipo)",
        peso: linha[chavePeso] ?? 0,
        pct: linha.pct ?? 0,
        pct_acumulado: linha.pct_acumulado ?? 0,
      })),
    },
    {
      id: "ranking_etapa",
      label: "Ranking por etapa",
      columns: COLUNAS_RANKING,
      rows: linhasRanking(payload.rankings?.por_etapa, ehTempo),
    },
    {
      id: "ranking_posto",
      label: "Ranking por posto",
      columns: COLUNAS_RANKING,
      rows: linhasRanking(payload.rankings?.por_posto, ehTempo),
    },
    {
      id: "ranking_responsavel",
      label: "Ranking por responsável",
      columns: COLUNAS_RANKING,
      rows: linhasRanking(payload.rankings?.por_responsavel, ehTempo),
    },
    {
      id: "custo",
      label: "Custo por categoria",
      columns: [
        { key: "categoria", label: "Categoria", type: "text" },
        { key: "custo_rs", label: "Custo (R$)", type: "currency" },
        { key: "horas", label: ehTempo ? "Horas perdidas" : "Horas estimadas", type: "number" },
        { key: "ocorrencias", label: "Ocorrências", type: "number" },
      ],
      rows: (payload.custo_por_categoria || []).map((c) => ({
        categoria: c.label || LABEL_CATEGORIA[c.categoria] || c.categoria,
        custo_rs: Number(c.custo_rs || 0),
        horas: Number(ehTempo ? c.horas_perdidas || 0 : c.horas_estimadas || 0),
        ocorrencias: c.ocorrencias ?? 0,
      })),
    },
    {
      id: "tabela_fonte",
      label: `Tabela-fonte (página ${payload.tabela_fonte?.pagina ?? 1} de ${
        Math.max(
          1,
          Math.ceil(
            (payload.tabela_fonte?.total || 0) /
              (payload.tabela_fonte?.por_pagina || 1),
          ),
        )
      })`,
      columns: ehTempo
        ? [
            { key: "pedido", label: "Pedido", type: "text" },
            { key: "ambiente", label: "Ambiente", type: "text" },
            { key: "fase", label: "Fase", type: "text" },
            { key: "etapa", label: "Etapa", type: "text" },
            { key: "posto", label: "Posto", type: "text" },
            { key: "responsavel", label: "Responsável", type: "text" },
            { key: "categoria", label: "Categoria", type: "text" },
            { key: "descricao", label: "Causa", type: "text" },
            { key: "minutos", label: "Atraso (min)", type: "number" },
            { key: "custo_rs", label: "Custo (R$)", type: "currency" },
            { key: "registrado_em", label: "Aberto em", type: "date" },
          ]
        : [
            { key: "pedido", label: "Pedido", type: "text" },
            { key: "peca", label: "Peça", type: "text" },
            { key: "ambiente", label: "Ambiente", type: "text" },
            { key: "posto", label: "Posto", type: "text" },
            { key: "operador", label: "Operador", type: "text" },
            { key: "categoria", label: "Categoria", type: "text" },
            { key: "tipo", label: "Tipo de defeito", type: "text" },
            { key: "qtd_boa", label: "Qtd boa", type: "number" },
            { key: "qtd_refugo", label: "Qtd refugo", type: "number" },
            { key: "registrado_em", label: "Registrado em", type: "date" },
          ],
      rows: (payload.tabela_fonte?.registros || []).map((r) =>
        ehTempo
          ? {
              pedido: r.pedido_numero || "",
              ambiente: `${r.ambiente_numero || ""} · ${r.ambiente_nome || ""}`,
              fase: r.fase || "",
              etapa: r.etapa?.label || "",
              posto: r.posto ? `${r.posto.nome} (${r.posto.numero})` : "",
              responsavel: r.responsavel_nome || "",
              categoria: LABEL_CATEGORIA[r.categoria] || r.categoria || "",
              descricao: r.descricao || "",
              minutos: r.minutos ?? 0,
              custo_rs: Number(r.custo_rs || 0),
              registrado_em: r.registrado_em || "",
            }
          : {
              pedido: r.pedido_numero || "",
              peca: r.peca_numero || "",
              ambiente: `${r.ambiente_numero || ""} · ${r.ambiente_nome || ""}`,
              posto: r.posto ? `${r.posto.nome} (${r.posto.numero})` : "",
              operador: r.operador_nome || "",
              categoria: LABEL_CATEGORIA[r.categoria] || r.categoria || "",
              tipo: r.tipo || "",
              qtd_boa: r.qtd_boa ?? 0,
              qtd_refugo: r.qtd_refugo ?? 0,
              registrado_em: r.registrado_em || "",
            },
      ),
    },
  ];

  return saver({
    titulo: `Análise de Atrasos — lente ${ehTempo ? "tempo" : "qualidade"}`,
    resumo,
    secoes,
  });
}
